import LegalLayout from "./LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="February 2026">
      <Section title="1. Acceptance of Terms">
        <p>
          By accessing or using the PROX service operated by Shadow Bridge Technologies, Inc.
          ("<strong>Company</strong>," "<strong>we</strong>," "<strong>us</strong>"), you agree to be
          bound by these Terms of Service. If you do not agree, do not use the service.
        </p>
      </Section>

      <Section title="2. Description of Service">
        <p>
          PROX is a privacy communication bridge that assigns proxy phone numbers to users and routes
          SMS/MMS messages through a Shadow Mapping system. This allows users to communicate with contacts
          without exposing their real phone number.
        </p>
        <p>
          The service includes: proxy number assignment, message forwarding via Shadow Numbers,
          contact management commands (SAVE, BURN, RESET), and vCard delivery.
        </p>
      </Section>

      <Section title="3. Eligibility">
        <p>
          You must be at least 18 years old and capable of forming a binding contract to use PROX.
          By using the service, you represent that you meet these requirements.
        </p>
      </Section>

      <Section title="4. Account and Number Assignment">
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Each user is assigned one proxy phone number upon registration.</li>
          <li>Proxy numbers remain active as long as your account is in good standing.</li>
          <li>You are responsible for all activity conducted through your assigned proxy number.</li>
          <li>Proxy numbers are the property of Shadow Bridge Technologies, Inc. and may be reclaimed upon account termination.</li>
        </ul>
      </Section>

      <Section title="5. Acceptable Use">
        <p>You agree <strong>not</strong> to use PROX to:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Send spam, unsolicited commercial messages, or bulk messaging.</li>
          <li>Harass, threaten, stalk, or abuse any person.</li>
          <li>Engage in illegal activity, fraud, or impersonation.</li>
          <li>Circumvent opt-out requests from contacts who have replied STOP.</li>
          <li>Violate any applicable local, state, national, or international law.</li>
          <li>Interfere with or disrupt the service infrastructure.</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate your account immediately for any violation
          of these terms.
        </p>
      </Section>

      <Section title="6. Message Routing and Delivery">
        <p>
          PROX routes messages through third-party telecommunications carriers. We do not guarantee:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>100% message delivery (carrier filtering, network issues, or recipient blocking may affect delivery).</li>
          <li>Delivery speed or latency targets.</li>
          <li>Preservation of rich media, formatting, or special message types beyond standard SMS/MMS.</li>
        </ul>
      </Section>

      <Section title="7. Commands and Features">
        <p>PROX supports the following user commands via SMS:</p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li><strong className="text-zinc-200 font-mono">BURN</strong> or <strong className="text-zinc-200 font-mono">RESET</strong> — Permanently deletes a mapping and blocks the associated contact.</li>
          <li><strong className="text-zinc-200 font-mono">SAVE [name]</strong> — Saves and labels a contact mapping.</li>
          <li><strong className="text-zinc-200 font-mono">HELP</strong> — Returns a list of available commands.</li>
        </ul>
        <p>Commands are case-insensitive and processed immediately upon receipt.</p>
      </Section>

      <Section title="8. Fees and Billing">
        <p>
          Service fees, if applicable, will be communicated at the time of registration or upon
          changes to your plan. Continued use after a pricing change constitutes acceptance of the
          new pricing.
        </p>
      </Section>

      <Section title="9. Privacy">
        <p>
          Your use of PROX is also governed by our{" "}
          <a href="/privacy" className="text-white underline underline-offset-2">Privacy Policy</a>.
          Please review it to understand how we collect, use, and protect your information.
        </p>
      </Section>

      <Section title="10. Disclaimer of Warranties">
        <p>
          PROX is provided "<strong>as is</strong>" and "<strong>as available</strong>" without warranties
          of any kind, whether express or implied, including but not limited to warranties of
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>
      </Section>

      <Section title="11. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, Shadow Bridge Technologies, Inc. shall not be liable
          for any indirect, incidental, special, consequential, or punitive damages, including loss of
          data, revenue, or business opportunities, arising from your use of the service.
        </p>
        <p>
          Our total liability for any claim arising from the service shall not exceed the amount you
          paid us in the 12 months preceding the claim.
        </p>
      </Section>

      <Section title="12. Indemnification">
        <p>
          You agree to indemnify and hold harmless Shadow Bridge Technologies, Inc., its officers,
          directors, employees, and agents from any claims, damages, or expenses arising from your use
          of the service or violation of these terms.
        </p>
      </Section>

      <Section title="13. Termination">
        <p>
          We may suspend or terminate your access to PROX at any time, with or without cause. Upon
          termination, your proxy number will be reclaimed and all active mappings will be burned.
          Provisions that by their nature should survive termination will survive.
        </p>
      </Section>

      <Section title="14. Governing Law">
        <p>
          These Terms shall be governed by the laws of the State of Delaware, without regard to
          conflict of law principles. Any disputes shall be resolved in the courts located in
          Delaware.
        </p>
      </Section>

      <Section title="15. Changes to Terms">
        <p>
          We reserve the right to modify these terms at any time. Material changes will be
          communicated via the service or email. Continued use after changes constitutes acceptance.
        </p>
      </Section>

      <Section title="16. Contact">
        <p>
          Shadow Bridge Technologies, Inc.<br />
          Email:{" "}
          <a href="mailto:legal@prox.day" className="text-white underline underline-offset-2">
            legal@prox.day
          </a>
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
